<template>
  <div class="root-container">
    <div class="table-container">
      <!-- 搜索栏（固定） -->
      <div class="search-container" :class="{ 'mobile-search-top': !isDesktop }">
        <!-- 电脑端 -->
        <div v-if="isDesktop" class="desktop-search-wrapper">
          <div class="search-row">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索新闻标题"
              prefix-icon="el-icon-search"
              clearable
              class="search-input"
              size="small"
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            ></el-input>
            <div class="button-group">
              <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd" class="add-button">
                添加
              </el-button>
              <el-button type="primary" size="small" icon="el-icon-filter" @click="toggleFilter" class="filter-button">
                {{ isFilterOpen ? '收起筛选' : '展开筛选' }}
              </el-button>
            </div>
          </div>

          <!-- 筛选面板 -->
          <div v-if="isFilterOpen" class="filter-panel">
            <div class="filter-row">
              <div class="filter-item">
                <label>分类：</label>
                <el-select v-model="filterForm.category_id" placeholder="全部分类" size="small" clearable>
                  <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id" />
                </el-select>
              </div>
              <div class="filter-item">
                <label>语言：</label>
                <el-select v-model="filterForm.language_code" placeholder="全部语言" size="small" clearable>
                  <el-option v-for="lang in languageList" :key="lang.code" :label="lang.name" :value="lang.code" />
                </el-select>
              </div>
            </div>
            <div class="filter-actions">
              <div class="applied-filters">
                <span v-if="!appliedFilterForm.category_id && !appliedFilterForm.language_code" class="filter-tag filter-tag-empty">
                  当前无筛选条件
                </span>
                <span v-if="appliedFilterForm.category_id" class="filter-tag">
                  {{ getCategoryName(appliedFilterForm.category_id) }}
                </span>
                <span v-if="appliedFilterForm.language_code" class="filter-tag">
                  {{ getLanguageName(appliedFilterForm.language_code) }}
                </span>
              </div>
              <div class="filter-buttons">
                <el-button size="small" @click="resetFilter">重置筛选</el-button>
                <el-button type="primary" size="small" @click="applyFilter">应用筛选</el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 手机端 -->
        <div v-else class="mobile-search-wrapper">
          <van-search
            v-model="searchKeyword"
            placeholder="搜索新闻标题"
            shape="round"
            @search="handleSearch"
            @clear="handleSearch"
          />
          <div class="mobile-add-button">
            <van-button type="primary" size="small" block icon="plus" @click="handleOpenAdd" style="margin-top: 10px;">
              添加
            </van-button>
          </div>
          <div v-if="isFilterOpen" class="mobile-filter-panel">
            <div class="mobile-applied-filters">
              <div v-if="!appliedFilterForm.category_id && !appliedFilterForm.language_code" class="applied-tags">
                <span class="mobile-filter-tag empty">当前无筛选条件</span>
              </div>
              <div v-else class="applied-tags">
                <span v-if="appliedFilterForm.category_id" class="mobile-filter-tag">
                  {{ getCategoryName(appliedFilterForm.category_id) }}
                </span>
                <span v-if="appliedFilterForm.language_code" class="mobile-filter-tag">
                  {{ getLanguageName(appliedFilterForm.language_code) }}
                </span>
              </div>
            </div>
            <div class="mobile-filter-form">
              <van-field label="分类" readonly :value="getCategoryName(filterForm.category_id)" placeholder="全部分类" is-link @click="showCategoryPicker = true" />
              <van-field label="语言" readonly :value="getLanguageName(filterForm.language_code)" placeholder="全部语言" is-link @click="showLanguagePicker = true" />
            </div>
            <div class="mobile-filter-actions">
              <van-button type="default" size="small" @click="resetFilter">重置</van-button>
              <van-button type="primary" size="small" @click="applyFilter">应用</van-button>
            </div>
          </div>
          <div class="mobile-filter-toggle" @click="toggleFilter">
            <van-icon :name="isFilterOpen ? 'arrow-up' : 'arrow-down'" />
            <span>{{ isFilterOpen ? '收起筛选' : '展开筛选' }}</span>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-container">
        <!-- 电脑端表格 -->
        <div class="table-wrapper" v-if="isDesktop">
          <el-table :data="dynamicList" style="width: 100%" tooltip-effect="dark" empty-text="暂无数据"
            class="desktop-table" :height="tableHeight">
            <el-table-column label="封面" prop="cover" align="center" width="100px">
              <template slot-scope="scope">
                <img v-if="scope.row.cover" :src="scope.row.cover" class="table-cover" />
                <span v-else class="no-cover">无</span>
              </template>
            </el-table-column>
            <el-table-column label="新闻标题" prop="title" align="left" width="260px" show-overflow-tooltip fixed>
              <template slot-scope="scope">
                <span>{{ scope.row.title }}</span>
              </template>
            </el-table-column>
            <el-table-column label="副标题" prop="sub_title" align="left" width="180px" show-overflow-tooltip>
            </el-table-column>
            <el-table-column label="分类" prop="category_name" align="center" width="120px">
            </el-table-column>
            <el-table-column label="语言" prop="language_name" align="center" width="120px">
              <template slot-scope="scope">
                {{ scope.row.language_name }}
              </template>
            </el-table-column>
            <el-table-column label="首页推荐" prop="is_home" align="center" width="100px">
              <template slot-scope="scope">
                <el-switch
                  :value="scope.row.is_home == 1"
                  @change="handleHomeSwitch(scope.row, $event)"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                ></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" prop="created_at" align="center" width="160px">
            </el-table-column>
            <el-table-column label="操作" align="right" fixed="right" width="155px">
              <template slot-scope="scope">
                <div class="table-actions">
                  <el-link @click="handleEdit(scope.row)">
                    <i class="el-icon-edit" style="font-size:16px;"></i>编辑
                  </el-link>
                  <el-dropdown @command="command($event, scope.row)">
                    <span class="el-dropdown-link">
                      更多操作
                      <i class="el-icon-arrow-down el-icon--right"></i>
                    </span>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="remove">删除</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片 -->
        <div class="mobile-cards" v-else>
          <div class="mobile-empty" v-if="dynamicList.length === 0">暂无数据</div>
          <div v-for="item in dynamicList" :key="item.id" class="mobile-card">
            <div class="card-header">
              <div class="card-title">
                <h3>{{ item.title }}</h3>
              </div>
            </div>
            <div class="card-content">
              <p v-if="item.sub_title"><strong>副标题：</strong>{{ item.sub_title }}</p>
              <p><strong>分类：</strong>{{ item.category_name || '-' }}</p>
              <p><strong>语言：</strong>{{ item.language_name }}</p>
              <p><strong>创建时间：</strong>{{ item.created_at }}</p>
            </div>
            <div class="card-actions">
              <van-button size="small" type="primary" @click="handleEdit(item)">编辑</van-button>
              <van-button size="small" type="danger" @click="handleDelete(item.id)">删除</van-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-container" v-if="isDesktop && total > 0">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="params.page"
          :page-sizes="[10, 20, 50]"
          :page-size="params.page_count"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        ></el-pagination>
      </div>
    </div>

    <!-- 添加/编辑对话框 -->
    <template v-if="isDesktop">
      <el-drawer
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        size="70%"
        :before-close="handleClose"
      >
        <add-dynamic
          :dynamic="editingItem"
          @save="handleSave"
          @cancel="dialogVisible = false"
        />
      </el-drawer>
    </template>
    <template v-else>
      <van-popup
        v-model="dialogVisible"
        position="bottom"
        :round="true"
        :style="{ height: '80%' }"
      >
        <div class="popup-header">
          <h3>{{ dialogTitle }}</h3>
          <van-icon name="cross" @click="dialogVisible = false" />
        </div>
        <div class="popup-content">
          <add-dynamic
            :dynamic="editingItem"
            @save="handleSave"
            @cancel="dialogVisible = false"
          />
        </div>
      </van-popup>
    </template>

    <!-- 手机端分类选择器 -->
    <van-popup v-model="showCategoryPicker" position="bottom" round>
      <van-picker
        :columns="categoryColumns"
        show-toolbar
        title="选择分类"
        @confirm="onCategoryConfirm"
        @cancel="showCategoryPicker = false"
      />
    </van-popup>
    <!-- 手机端语言选择器 -->
    <van-popup v-model="showLanguagePicker" position="bottom" round>
      <van-picker
        :columns="languageColumns"
        show-toolbar
        title="选择语言"
        @confirm="onLanguageConfirm"
        @cancel="showLanguagePicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import AddDynamic from './components/add-dynamic.vue'
import { getNewsList, deleteNews } from '@/api/website'
import { getLanguage } from '@/api/public'

export default {
  name: 'DynamicPage',
  components: {
    AddDynamic
  },
  data() {
    return {
      isDesktop: true,
      searchKeyword: '',
      isFilterOpen: false,
      filterForm: {
        category_id: '',
        language_code: ''
      },
      appliedFilterForm: {
        category_id: '',
        language_code: ''
      },
      categoryList: [],
      languageList: [],
      showCategoryPicker: false,
      showLanguagePicker: false,
      dialogVisible: false,
      dialogTitle: '',
      editingItem: null,
      dynamicList: [],
      total: 0,
      params: {
        page: 1,
        page_count: 10
      }
    }
  },
  computed: {
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      // 计算表格高度：减去顶部栏、筛选区域、分页栏和底部的高度
      const headerHeight = 60 // 顶部栏高度
      const searchHeight = this.isFilterOpen ? 200 : 60 // 搜索栏高度（包含筛选面板）
      const paginationHeight = 60 // 分页栏高度
      const footerHeight = 40 // 底部高度
      const padding = 20 // 页面内边距
      return window.innerHeight - headerHeight - searchHeight - paginationHeight - footerHeight - padding - 120 // 100是其他元素的额外高度
    },
    categoryColumns() {
      return this.categoryList.map(function(c) {
        return { text: c.name, value: c.id }
      })
    },
    languageColumns() {
      return this.languageList.map(function(l) {
        return { text: l.name, value: l.code }
      })
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.appliedFilterForm = JSON.parse(JSON.stringify(this.filterForm))
    this.getLanguageList()
    this.getCategoryList()
    this.getTableData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.checkScreenSize()
    },
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    handleSearch() {
      this.params.page = 1
      this.getTableData()
    },
    getTableData() {
      var params = {
        page: this.params.page,
        page_count: this.params.page_count
      }
      if (this.searchKeyword) {
        params.keyword = this.searchKeyword
      }
      if (this.appliedFilterForm.category_id) {
        params.category_id = this.appliedFilterForm.category_id
      }
      if (this.appliedFilterForm.language_code) {
        params.language_code = this.appliedFilterForm.language_code
      }
      getNewsList(params).then(function(res) {
        if (res.code === 200) {
          this.dynamicList = res.data.data || []
          this.total = res.data.total || 0
        }
      }.bind(this))
    },
    handleSizeChange(val) {
      this.params.page_count = val
      this.params.page = 1
      this.getTableData()
    },
    handleCurrentChange(val) {
      this.params.page = val
      this.getTableData()
    },
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen
    },
    applyFilter() {
      this.appliedFilterForm = JSON.parse(JSON.stringify(this.filterForm))
      this.params.page = 1
      this.getTableData()
    },
    resetFilter() {
      this.filterForm = { category_id: '', language_code: '' }
      this.appliedFilterForm = { category_id: '', language_code: '' }
      this.params.page = 1
      this.getTableData()
    },
    handleOpenAdd() {
      this.dialogTitle = '添加新闻'
      this.editingItem = null
      this.dialogVisible = true
    },
    handleAdd() {
      this.dialogTitle = '添加新闻'
      this.editingItem = null
      this.dialogVisible = true
    },
    handleEdit(item) {
      this.dialogTitle = '编辑新闻'
      this.editingItem = JSON.parse(JSON.stringify(item))
      this.dialogVisible = true
    },
    handleClose() {
      this.dialogVisible = false
    },
    command(cmd, row) {
      if (cmd === 'remove') {
        this.handleDelete(row.id)
      }
    },
    handleDelete(id) {
      this.$confirm('确定要删除吗？', '确定', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(function() {
        deleteNews(id).then(function(res) {
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.getTableData()
          }
        }.bind(this))
      }.bind(this)).catch(function() {})
    },
    handleHomeSwitch(row, val) {
      var self = this
      // 使用 updateNews 接口更新
      var api = require('@/api/website')
      api.updateNews(row.id, { is_home: val ? 1 : 0 }).then(function(res) {
        if (res.code === 200) {
          self.$message.success(val ? '已推荐到首页' : '已取消推荐')
          self.getTableData()
        }
      })
    },
    handleSave() {
      this.dialogVisible = false
      this.getTableData()
      this.$message.success('保存成功')
    },
    async getLanguageList() {
      try {
        var response = await getLanguage()
        this.languageList = response.data || []
      } catch (error) {
        console.error('获取语言列表失败:', error)
      }
    },
    getCategoryList() {
      var api = require('@/api/website')
      api.getNewsCategories().then(function(res) {
        if (res.code === 200) {
          var categories = []
          res.data.forEach(function(item) {
            var language = ''
            if (item.translations) {
              Object.keys(item.translations).forEach(function(key) {
                if (language === '' && item.translations[key].name) {
                  language = item.translations[key].name
                }
              })
            }
            categories.push({ id: item.id, name: language || '未命名' })
          })
          this.categoryList = categories
        }
      }.bind(this))
    },
    getCategoryName(id) {
      if (!id) return ''
      var cat = this.categoryList.find(function(c) { return c.id === id })
      return cat ? cat.name : ''
    },
    getLanguageName(code) {
      if (!code) return ''
      var lang = this.languageList.find(function(l) { return l.code === code })
      return lang ? lang.name : ''
    },
    onCategoryConfirm(val) {
      this.filterForm.category_id = val.value
      this.showCategoryPicker = false
    },
    onLanguageConfirm(val) {
      this.filterForm.language_code = val.value
      this.showLanguagePicker = false
    }
  }
}
</script>

<style scoped lang="scss">
.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-container {
  margin-bottom: 0;
  padding: 20px;
  background-color: var(--background);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.mobile-search-top {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  padding: 10px 15px;
}

.search-container h2 {
  margin-bottom: 20px;
  color: var(--text);
}

.desktop-search-wrapper {
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.search-input {
  width: 100%;
  max-width: 400px;
  font-size: var(--font-size-sm);
}

.filter-button {
  white-space: nowrap;
}

.button-group {
  display: flex;
  align-items: center;
}

.add-button {
  white-space: nowrap;
}

.mobile-add-button {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  padding: 15px;
  margin-top: 10px;
  box-shadow: var(--shadow);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--border);
}

.applied-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  display: inline-flex;
  align-items: center;
}

.filter-tag-empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

.filter-buttons {
  display: flex;
  gap: 10px;
}

.filter-item label {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text);
}

.mobile-search-wrapper {
  width: 100%;
}

.mobile-filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  margin-top: 10px;
  overflow: hidden;
}

.mobile-applied-filters {
  padding: 10px 15px;
  background-color: var(--bg-100);
  border-radius: var(--border-radius);
  margin-bottom: 15px;
}

.mobile-applied-filters .applied-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  display: inline-block;
}

.mobile-filter-tag.empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

.mobile-filter-form {
  padding: 0 10px;
}

.mobile-filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px;
  background-color: var(--background);
}

.mobile-filter-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  margin-top: 10px;
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: var(--text-200);
  cursor: pointer;
  transition: all 0.2s ease;
}

.content-container {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 20px 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  height: 100%;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  font-size: 14px;
}

.table-actions {
  width: 100%;
  display: inline-flex;
  justify-content: flex-end;
  gap: 10px;
  font-size: 14px;
}

.desktop-table {
  height: 100%;
  margin-bottom: 0;
  font-size: 14px;
}

.table-cover {
  width: 60px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.no-cover {
  color: #ccc;
  font-size: 12px;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

.mobile-card {
  border: 1px solid var(--border);
  border-radius: var(--border-radius-lg);
  padding: 15px;
  background-color: var(--bg-200);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.mobile-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

.card-title h3 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-content {
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.card-content p {
  margin: 8px 0;
  font-size: var(--font-size-sm);
  color: var(--text-200);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  line-height: 1.6;
  flex-wrap: wrap;
}

.card-content p strong {
  color: var(--text);
  font-weight: 500;
  min-width: 80px;
  text-align: left;
  flex-shrink: 0;
}

.card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.mobile-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--text-200);
  font-size: var(--font-size-sm);
}

.pagination-container {
  margin-top: 0;
  margin-bottom: 0;
  padding: 10px;
  border-top: 1px solid var(--border);
  text-align: right;
  box-sizing: border-box;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: var(--background);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.popup-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text);
}

.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--background);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-container h2 {
    margin-bottom: 15px;
  }

  .content-container {
    padding: 10px 0;
  }

  .search-input {
    max-width: 100%;
  }

  .pagination-container {
    text-align: center;
    padding: 15px;
  }

  .filter-row {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .filter-item {
    width: 100%;
  }
}

@media (min-width: 769px) {
  .search-container .mobile-search-container,
  .mobile-filter-toggle {
    display: none;
  }

  .desktop-table {
    display: block;
  }

  .mobile-cards {
    display: none;
  }
}

@media (max-width: 768px) {
  .search-container {
    width: 100vw;
    margin-left: -15px;
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    background-color: var(--bg-200);
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: flex;
  }

  .search-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-input {
    max-width: 100%;
  }
}
</style>
